import { Fragment, useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import { EInvoiceReadError, eInvoiceFormatLabel, eInvoiceReadErrorText, eInvoiceSummaryDeviations, readEInvoiceFile, readInvoicePdf, smallBusinessOn, taxIdLabel, type EInvoiceExpected, type EInvoiceFinding } from "@talentkontor/domain";
import { ErrorState, Loading } from "../../components/States";
import { useAction } from "../../components/ui";
import { toast } from "../../components/Toast";
import { api } from "../../data";
import { money } from "../../lib/format";
import { parseCentsInput, parseQuantityInput, quantityText } from "../../lib/betrag";
import { useArticles, useCreatorBase, useInvoiceDetails, useProfileExtras, type CDeal, type CPeriod } from "./data";
import {
  amountsAreShare, checkDraft, computeDraft, creatorRateOptions, creatorRules, linesAfterSave, dealChoices, defaultServicePeriod, draftStart, extrasOfPeriod, draftFromInvoice, invoiceHeader, invoiceLineRows, invoicesWithoutDeduction, isDirect, rateOf,
  recipientOf, newDraft, newLineKey, openQuestions, suggestLine, OTHER_NOTE_MIN, pickPeriod, profileFieldLabel, profileGaps, vatWord, TAX_CONFIRM_KEY,
  type DraftCheck, type DraftLine, type DraftPurpose, type InvoiceDraft, type Message,
} from "./model";
import { attachInvoiceFile, checkUpload, loadAgencyContact, loadTaxHistory, neverSubmitted, removeInvoiceFile, saveInvoiceDraft, submitInvoice } from "./actions";
import { confirmFindingFor, creatorEInvoice, creatorEInvoiceOffer, draftSuggestionFromEInvoice, eInvoiceAttachmentOf, splitEInvoiceFindings, type CreatorEInvoiceInput } from "./eRechnung";
import { BELEG_ACCEPT, eInvoicePruefHinweis, EingabeFehler, isXmlUpload } from "../../model/e-rechnung";
import { creatorHasQrBill, creatorInvoicePdf } from "./rechnungPdf";
import { DemoNote, openDocument, SelectFullText, useCreatorContext } from "./ui";
import { dateOf, monthOf, pctOf, useT, type Lang } from "./sprache";
import { useUnsaved } from "./ungespeichert";
import { steuerVorschlagHinweis } from "../../model/steuer-verantwortung";

/**
 * Rechnung einreichen (12_app_creator/rechnung-einreichen-1 … 3.html) als Schrittfolge mit dem Umfang des bisherigen
 * Rechnungs-Generators: 1. Zuordnung (Kooperation, Leistungsmonat oder Nachberechnung/Auslagen), 2. Positionen mit Steuer
 * und Prüfungen, 3. Rechnung (aus den Angaben erzeugen oder eigene Datei hochladen), 4. Prüfen und einreichen.
 * Entwürfe bleiben gespeichert.
 */

function centsInput(cents: number, lang: Lang): string {
  return (cents / 100).toLocaleString(lang === "en" ? "en-GB" : "de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function RechnungEinreichen() {
  const { scope, today, userId, lang } = useCreatorContext();
  const t = useT();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const base = useCreatorBase(scope);
  const profile = useProfileExtras(scope, userId);
  const articles = useArticles(scope);
  const taxHistory = useQuery({ queryKey: ["tax_status_history", scope.talentId], queryFn: () => loadTaxHistory(api, scope.talentId) });
  const invoiceId = params.get("rechnung");
  const details = useInvoiceDetails(scope, invoiceId ? [invoiceId] : []);
  const step = Math.min(4, Math.max(1, Number(params.get("schritt") ?? (invoiceId ? 2 : 1)) || 1));
  const [edited, setEdited] = useState<InvoiceDraft | null>(null);
  // Ungespeicherte Änderungen aus Schritt 1 und 2: Datei und Prüfen erst nach „Weiter“ (speichert den Entwurf)
  const [dirty, setDirty] = useState(false);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [showCheck, setShowCheck] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [pdfMatches, setPdfMatches] = useState(false);
  const [comment, setComment] = useState("");
  const [priceText, setPriceText] = useState<Record<string, string>>({});
  // Gegenprüfung GB-4: Mengen als Text (wie die Preise), damit Unlesbares als Fehler gilt statt still 0
  const [qtyText, setQtyText] = useState<Record<string, string>>({});
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  // L12: Hinweis, wenn die XRechnung zur erzeugten Rechnung nicht entstehen konnte (was fehlt)
  const [eInvoiceNote, setEInvoiceNote] = useState<string | null>(null);
  // PARITAET-R3-3: eingelesene Rechnung (Vorschlag in Schritt 2, dieselbe Datei als Original in Schritt 3)
  const [imported, setImported] = useState<{ file: File; grossCents: number | null; currency: string | null; filled: string[] } | null>(null);
  const { run, busy } = useAction();
  const b = base.data;
  useUnsaved("rechnung", dirty);

  const existing = b && invoiceId ? b.invoices.find((i) => i.id === invoiceId) ?? null : null;
  const editable = !existing || ["draft", "revision_requested", "rejected"].includes(existing.status);

  // Startstand des Entwurfs aus den Daten; ab der ersten Änderung gilt der bearbeitete Stand
  const dealParam = params.get("kooperation");
  const monthParam = params.get("monat");
  const purposeParam = params.get("zweck");
  const initial = useMemo(() => {
    if (!b) return null;
    if (invoiceId) {
      if (!existing || !details.data) return null;
      return draftFromInvoice(existing, details.data.lines.filter((l) => l.invoice_id === existing.id));
    }
    return newDraft(b, today, dealParam, monthParam, lang, purposeParam === "extra" || purposeParam === "expenses" || purposeParam === "other" ? purposeParam : "month");
    // Der Startstand soll sich nach dem Laden nicht mehr ändern (neue Zeilenschlüssel); darum ohne `existing`-Objekt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b === undefined, invoiceId, existing?.id, details.data === undefined, today, dealParam, monthParam, purposeParam]);
  const draft = edited ?? initial;
  const setDraft = (fn: (d: InvoiceDraft | null) => InvoiceDraft | null) => {
    setDirty(true);
    setEdited((prev) => fn(prev ?? initial));
  };

  const env = useMemo(() => {
    if (!b || !draft) return null;
    const deal = b.deals.find((d) => d.id === draft.dealId) ?? null;
    const purpose = draft.purpose ?? "month";
    const period = purpose === "month" ? b.periods.find((p) => p.id === draft.periodId) ?? null : null;
    // Positionen ohne Monat gehören zum ersten Monat (nach Monat, nicht nach Ladereihenfolge)
    const extras = deal && period ? extrasOfPeriod(b.lineItems, b.periods, period) : [];
    const rules = creatorRules(b.defaults, b.org);
    const current = draft.invoiceId ? b.invoices.find((i) => i.id === draft.invoiceId) : undefined;
    const files = (details.data?.attachments ?? []).filter((a) => a.owner_id === draft.invoiceId);
    // Steuerstatus zum Leistungsdatum, nicht der heutige Schalter (Wechsel zum Jahreswechsel, rückwirkende Änderung)
    const taxDay = draft.serviceTo ?? draft.serviceFrom ?? draft.issueDate;
    const talent = { ...b.talent, small_business: smallBusinessOn(taxHistory.data, taxDay, b.talent.small_business) };
    return {
      today, timeZone: b.org.timezone, talent, payout: profile.data?.payout ?? null, org: b.org, rules, invoices: b.invoices,
      deal, period, extras, hasPdf: !!current?.pdf_path, recipient: recipientOf(b, deal, lang), rate: deal ? rateOf(b, deal, period?.month) : null,
      ownNumbers: b.ownNumbers, hasReceipt: files.some((a) => a.storage_path !== current?.pdf_path),
      pdfOrigin: current?.pdf_origin ?? null, pdfConfirmed: pdfMatches || !!current?.pdf_confirmed_at, lang,
    };
  }, [b, draft, today, profile.data, details.data, pdfMatches, lang, taxHistory.data]);

  if (base.error) return <ErrorState error={base.error} retry={() => void base.refetch()} />;
  if (base.isPending || !b) return <Loading />;
  if (invoiceId && !existing) {
    return (
      <Frame step={step} reachable={0} params={params}>
        <div className="panel stack tk-empty" role="alert">
          <h3>{t("Diese Rechnung gibt es in deiner Agentur nicht.", "This invoice does not exist in your agency.")}</h3>
          <p>{t("Sie gehört zu einer anderen Agentur oder wurde entfernt.", "It belongs to another agency or has been removed.")}</p>
          <Link className="btn" to="/creator/rechnungen">{t("Zu deinen Rechnungen", "Go to your invoices")}</Link>
        </div>
      </Frame>
    );
  }
  if (existing && !editable) {
    return (
      <Frame step={step} reachable={0} params={params}>
        <div className="panel stack tk-empty">
          {/* Überschrift je Stand (Befund Creator 13): „freigegeben“ ist nicht „eingereicht“ */}
          <h3>{existing.status === "approved" ? t("Diese Rechnung ist freigegeben.", "This invoice has been approved.")
            : existing.status === "sent" ? t("Diese Rechnung ist in der Auszahlung.", "This invoice is being paid out.")
            : existing.status === "paid" ? t("Diese Rechnung ist bezahlt.", "This invoice has been paid.")
            : existing.status === "cancelled" ? t("Diese Rechnung ist storniert.", "This invoice has been cancelled.")
            : existing.status === "rejected" ? t("Diese Rechnung wurde abgelehnt.", "This invoice was rejected.")
            : t("Diese Rechnung ist eingereicht.", "This invoice has been submitted.")}</h3>
          <p>{existing.status === "submitted" && !existing.reviewed_at
            ? t("Solange deine Agentur sie noch nicht geprüft hat, kannst du die Einreichung im Rechnungsstatus zurückziehen und die Rechnung dann ändern.", "As long as your agency has not reviewed it, you can withdraw the submission in the invoice status and then change the invoice.")
            : ["approved", "sent"].includes(existing.status)
              ? t("Ändern kann sie jetzt nur noch deine Agentur. Im Rechnungsstatus kannst du mit „Korrektur anfragen“ um eine Korrektur bitten.", "Only your agency can change it now. In the invoice status you can ask for a correction with “Request a correction”.")
              : t("Ändern kann sie jetzt nur noch deine Agentur, zum Beispiel indem sie sie zur Korrektur zurückgibt.", "Only your agency can change it now, for example by returning it for correction.")}</p>
          <Link className="btn" to={`/creator/rechnungen?rechnung=${existing.id}`}>{t("Zum Rechnungsstatus", "Go to invoice status")}</Link>
        </div>
      </Frame>
    );
  }
  if (!draft || !env) return <Loading />;

  const purpose: DraftPurpose = draft.purpose ?? "month";
  const choices = dealChoices(b, lang);
  const chosenDeal = [...choices.open, ...choices.done].find((c) => c.deal.id === draft.dealId)?.label ?? null;
  const brandName = (d: CDeal | null) => (d ? b.brands.get(d.brand_id)?.name ?? t("Marke", "Brand") : "");
  const comp = computeDraft(draft, env.talent, b.org, env.recipient);
  // Abnahme E-2: Steuersatz je Position (Sätze des Landes am Leistungsdatum); ohne Steuerausweis keine Auswahl
  const rateOptions = creatorRateOptions(env.talent.country, comp.tax.ratePercent, draft.month ? `${draft.month}-01` : draft.serviceTo ?? draft.issueDate);
  const lineRate = (l: DraftLine) => (typeof l.rateBp === "number" && l.rateBp >= 0 ? l.rateBp : Math.round(comp.tax.ratePercent * 100));
  const direkt = isDirect(env.deal);
  // CREATOR-1: Agentur zeigt nur den Anteil (0079): Beträge sind schon dein Anteil, kein Budgetmodus
  const nurAnteil = amountsAreShare(env.deal);
  const ohneAbzug = invoicesWithoutDeduction(env.deal);
  // Gegenprüfung GB-4: unlesbarer Preis oder unlesbare Menge sperrt Speichern und Einreichen (vorher still 0, die Position fiel weg)
  const unreadable = draft.lines.flatMap((l, i) => {
    const p = priceText[l.key];
    const q = qtyText[l.key];
    const price = p === undefined ? null : parseCentsInput(p);
    const qty = q === undefined ? null : parseQuantityInput(q);
    return [
      ...(price !== null && (!Number.isFinite(price) || price < 0)
        ? [{ key: `line_price_${l.key}`, text: t(`Position ${i + 1}: Der Preis „${p!.trim()}“ ist nicht lesbar (etwa 1.234,50 oder 1234.50).`, `Item ${i + 1}: the price “${p!.trim()}” cannot be read (e.g. 1,234.50 or 1234,50).`) }] : []),
      ...(qty !== null && !Number.isFinite(qty)
        ? [{ key: `line_qty_${l.key}`, text: t(`Position ${i + 1}: Die Menge „${q!.trim()}“ ist nicht lesbar (etwa 2 oder 1,5).`, `Item ${i + 1}: the quantity “${q!.trim()}” cannot be read (e.g. 2 or 1.5).`) }] : []),
    ];
  });
  const withUnreadable = (c: ReturnType<typeof checkDraft>) => (unreadable.length ? { ...c, errors: [...unreadable, ...c.errors] } : c);
  const saveCheck = withUnreadable(checkDraft(draft, env, "save"));
  const submitCheck = withUnreadable(checkDraft(draft, env, "submit"));
  const unconfirmed = openQuestions(submitCheck, confirmed);
  const rate = env.rate;
  const saved = !!draft.invoiceId;
  const assigned = purpose === "other" ? (draft.note ?? "").trim().length >= OTHER_NOTE_MIN : !!draft.dealId && (purpose !== "month" || !!draft.periodId);
  const reachable = !assigned ? 1 : saved && !dirty ? (env.hasPdf ? 4 : 3) : 2;
  const cur = draft.currency;
  const fmt = (d: string | null | undefined) => dateOf(d ?? null, lang, b.org.timezone);

  const goto = (n: number, id?: string) => {
    const next = new URLSearchParams(params);
    next.set("schritt", String(n));
    if (id) next.set("rechnung", id);
    setParams(next);
    setShowCheck(false);
    window.scrollTo?.({ top: 0 });
  };
  const patch = (p: Partial<InvoiceDraft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchLine = (key: string, p: Partial<DraftLine>) => setDraft((d) => (d ? { ...d, lines: d.lines.map((l) => (l.key === key ? { ...l, ...p } : l)) } : d));

  // Zweck wechseln: bei einer Rechnung ohne Kooperation die erste Kooperation vorschlagen
  const firstDealId = choices.open[0]?.deal.id ?? choices.done[0]?.deal.id ?? null;
  function pickPurpose(p: DraftPurpose) {
    const id = draft?.dealId ?? firstDealId;
    if (id) chooseDealAndMonth(id, null, p);
  }

  /** Sonstige Rechnung an die Agentur ohne Kooperation (PARITAET-5): eigener Betrag, keine Provision, Beschreibung Pflicht */
  function chooseOther() {
    setDraft((d) => d && ({
      ...d, purpose: "other", dealId: null, periodId: null, month: null, currency: b!.org.currency, note: d.note ?? "",
      lines: d.invoiceId ? d.lines : [{ key: newLineKey(), description: "", quantity: 1, unitCents: 0, commissionFree: false }],
      mode: "share", commissionPercent: null, serviceFrom: d.serviceFrom ?? today, serviceTo: d.serviceTo ?? today,
    }));
    setPriceText({}); setQtyText({});
    setConfirmed(new Set());
  }

  function chooseDealAndMonth(dealId: string, periodId: string | null, nextPurpose: DraftPurpose = purpose) {
    const deal = b!.deals.find((d) => d.id === dealId) ?? null;
    const periods = deal ? b!.periods.filter((p) => p.deal_id === deal.id).sort((x, y) => x.month.localeCompare(y.month)) : [];
    const r = deal ? rateOf(b!, deal) : null;
    const direct = invoicesWithoutDeduction(deal);
    if (nextPurpose !== "month") {
      // Nachberechnung oder Auslagen: ohne Monat, Zeitraum frei; Auslagen sind provisionsfrei und werden voll gestellt
      setDraft((d) => d && ({
        ...d, purpose: nextPurpose, dealId, periodId: null, month: null, currency: deal?.currency ?? d.currency,
        lines: d.invoiceId ? d.lines : [{ key: newLineKey(), description: "", quantity: 1, unitCents: 0, commissionFree: nextPurpose === "expenses" }],
        mode: nextPurpose === "expenses" || direct ? "share" : d.invoiceId ? d.mode : "budget",
        commissionPercent: nextPurpose === "expenses" || direct ? null : d.invoiceId ? d.commissionPercent : r?.percent ?? null,
        serviceFrom: d.serviceFrom ?? today, serviceTo: d.serviceTo ?? today,
      }));
    } else {
      const period = periods.find((p) => p.id === periodId) ?? pickPeriod(periods, b!, today, null);
      const extras = deal && period ? extrasOfPeriod(b!.lineItems, b!.periods, period) : [];
      const month = period?.month.slice(0, 7) ?? null;
      const service = month ? defaultServicePeriod(month, today) : null;
      setDraft((d) => d && ({
        ...d,
        purpose: "month", dealId, periodId: period?.id ?? null, month, currency: deal?.currency ?? d.currency,
        // Bei einer bestehenden Rechnung bleiben die eigenen Positionen stehen; neu: Vorschläge der Agentur (ohne weiteren Abzug,
        // GELD-1) oder Monatsbetrag mit dem vereinbarten Satz (draftStart)
        ...(d.invoiceId ? { lines: d.lines, mode: d.mode, commissionPercent: d.commissionPercent }
          : deal && period ? draftStart(deal, period, extras, brandName(deal), env!.recipient.language, rateOf(b!, deal, period.month))
            : { lines: [], mode: direct ? "share" as const : "budget" as const, commissionPercent: direct ? null : r?.percent ?? null }),
        serviceFrom: service?.from ?? d.serviceFrom, serviceTo: service?.to ?? d.serviceTo,
      }));
    }
    setPriceText({}); setQtyText({});
    setConfirmed(new Set());
  }

  async function saveAndContinue() {
    setShowCheck(true);
    if (saveCheck.errors.length) return;
    const header = invoiceHeader(draft!, comp, { ...scope, direct: direkt, brandId: env?.deal?.brand_id ?? null });
    const before = draft!.invoiceId ? b!.invoices.find((i) => i.id === draft!.invoiceId) : undefined;
    const id = await run("speichern", async () => {
      const saved = await saveInvoiceDraft(api, scope, { invoiceId: draft!.invoiceId, header, lines: (x) => invoiceLineRows(comp, scope.orgId, x), userId });
      // Eine aus den Angaben erzeugte Rechnung folgt den Änderungen: neu erzeugen, damit Beleg und Angaben gleich bleiben
      if (before?.pdf_origin === "generated") {
        const input = {
          invoice: { ...before, ...(header as Partial<typeof before>), id: saved },
          lines: invoiceLineRows(comp, scope.orgId, saved).map((l) => ({ description: String(l.description), quantity: Number(l.quantity), unit_cents: Number(l.unit_cents), net_cents: Number(l.net_cents), tax_rate_bp: Number(l.tax_rate_bp) })),
          talent: b!.talent, payout: profile.data?.payout ?? null, recipient: env!.recipient,
        };
        const f = creatorInvoicePdf(input);
        await attachInvoiceFile(api, scope, saved, new Blob([f.bytes as BlobPart], { type: "application/pdf" }), f.fileName, true, "generated");
        // L12: die XRechnung folgt der erzeugten Rechnung (die alte Fassung hat attachInvoiceFile eben entfernt)
        await attachEInvoiceCopy(saved, input).catch(() => null);
      }
      return saved;
    }, draft!.invoiceId ? (before?.pdf_origin === "generated" ? t("Entwurf gespeichert, Rechnung neu erzeugt.", "Draft saved, invoice created again.") : t("Entwurf gespeichert.", "Draft saved.")) : t("Entwurf angelegt.", "Draft created."));
    if (!id) return;
    // Gespeicherte Positionen sind jetzt dein Anteil (nach Provision); weiteres Bearbeiten ohne erneuten Abzug
    setEdited((d) => {
      const c = d ?? initial;
      return c && ({ ...c, invoiceId: id, mode: "share", commissionPercent: null,
        lines: linesAfterSave(comp) });
    });
    setDirty(false);
    setPriceText({}); setQtyText({});
    setPdfMatches(false);
    goto(3, id);
  }

  const currentRow = draft.invoiceId ? b.invoices.find((i) => i.id === draft.invoiceId) : undefined;
  const savedLines = (details.data?.lines ?? []).filter((l) => l.invoice_id === draft.invoiceId);
  const pdfInput = currentRow && savedLines.length ? { invoice: currentRow, lines: savedLines, talent: b.talent, payout: profile.data?.payout ?? null, recipient: env.recipient } : null;

  // ── Steuerprüfung L12: XRechnung der eigenen Rechnung (regelbesteuerte Creator in Deutschland) und eingereichte E-Rechnungen ──
  const eOffer = creatorEInvoiceOffer(env.talent, currentRow ?? null, env.recipient.country);
  const directBilling = env.deal && isDirect(env.deal) ? b.billing.get(env.deal.id) ?? null : null;
  async function eInvoiceOf(input: Omit<CreatorEInvoiceInput, "recipientEmail" | "reference">) {
    // Elektronische Adresse des Empfängers: Rechnungsadresse der Marke bzw. Antwortadresse der Agentur
    const email = directBilling ? directBilling.email ?? null : (await loadAgencyContact(api, scope.orgId))?.email ?? null;
    return creatorEInvoice({ ...input, talent: { ...input.talent, small_business: env!.talent.small_business }, recipientEmail: email, reference: directBilling?.reference ?? null });
  }
  /** XRechnung zur erzeugten Rechnung mit ablegen (nur an Empfänger in Deutschland). Gibt zurück, was fehlt, sonst null. */
  async function attachEInvoiceCopy(invoiceId: string, input: Omit<CreatorEInvoiceInput, "recipientEmail" | "reference">): Promise<string | null> {
    if (!creatorEInvoiceOffer(env!.talent, input.invoice, env!.recipient.country).attach) return null;
    const x = await eInvoiceOf(input);
    if (!x.ok) {
      const missing = [...new Set(x.problems.map((p) => p.text))].join(" ");
      const note = t(`Die XRechnung wurde nicht erzeugt, es fehlt: ${missing}`, `The XRechnung was not created, missing: ${missing}`);
      setEInvoiceNote(note);
      return note;
    }
    setEInvoiceNote(null);
    await attachInvoiceFile(api, scope, invoiceId, new Blob([x.xml], { type: "application/xml" }), x.filename, true, "generated", { replaces: false, lang });
    return null;
  }
  async function downloadEInvoice() {
    if (!pdfInput) return;
    const x = await eInvoiceOf(pdfInput);
    if (!x.ok) return void toast(t(`Für die XRechnung fehlt noch: ${[...new Set(x.problems.map((p) => p.text))].join(" ")}`, `The XRechnung still needs: ${[...new Set(x.problems.map((p) => p.text))].join(" ")}`), "alert");
    const url = URL.createObjectURL(new Blob([x.xml], { type: "application/xml" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = x.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  // Befund 6: die Rechnung aus den gespeicherten Angaben erzeugen und als Beleg ablegen
  async function generate() {
    if (!pdfInput || !draft?.invoiceId) return;
    const f = creatorInvoicePdf(pdfInput);
    const input = pdfInput;
    const path = await run("erzeugen", async () => {
      const p = await attachInvoiceFile(api, scope, draft.invoiceId!, new Blob([f.bytes as BlobPart], { type: "application/pdf" }), f.fileName, true, "generated");
      await attachEInvoiceCopy(draft.invoiceId!, input);
      return p;
    }, eOffer.attach ? t("Rechnung erzeugt und abgelegt, mit XRechnung.", "Invoice created and saved, with XRechnung.") : t("Rechnung erzeugt und abgelegt.", "Invoice created and saved."));
    if (path) setUploadStatus(t(`Erzeugt: ${f.fileName}`, `Created: ${f.fileName}`));
  }
  function preview() {
    if (!pdfInput) return;
    const f = creatorInvoicePdf(pdfInput);
    const url = URL.createObjectURL(new Blob([f.bytes as BlobPart], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>, asInvoice: boolean) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft?.invoiceId) return;
    const problem = checkUpload(file, lang);
    if (problem) {
      setUploadStatus(problem);
      toast(problem, "alert", "creator:upload", { melden: false });
      return;
    }
    setUploadStatus(t(`${file.name} wird hochgeladen …`, `Uploading ${file.name} …`));
    const path = await run("upload", () => attachInvoiceFile(api, scope, draft.invoiceId!, file, file.name, asInvoice, "uploaded", { lang, onWarning: (w) => toast(w, "alert", "creator:upload-hinweis", { melden: false }) }),
      asInvoice ? t("Rechnung hochgeladen.", "Invoice uploaded.") : t("Beleg angehängt.", "Document attached."));
    if (path && asInvoice) setPdfMatches(false);
    setUploadStatus(path ? t(`Hochgeladen: ${file.name}`, `Uploaded: ${file.name}`) : t("Die Datei wurde nicht hochgeladen. Nutze PDF, JPG, PNG oder eine E-Rechnung (XML) mit maximal 15 MB (XML 5 MB).", "The file was not uploaded. Use PDF, JPG, PNG or an e-invoice (XML) with at most 15 MB (XML 5 MB)."));
  }

  /** Bestehende Rechnung (PDF) einlesen: Nummer, Daten, Zahlungsziel und Positionen als Vorschlag in den Entwurf */
  async function onImport(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft || !env) return;
    const xml = isXmlUpload(file);
    if (file.type && file.type !== "application/pdf" && !xml) return void toast(t("Bitte eine PDF-Datei oder eine E-Rechnung (XML) wählen.", "Please choose a PDF file or an e-invoice (XML)."), "alert", "creator:einlesen", { melden: false });
    const problem = checkUpload(file, lang);
    if (problem) return void toast(problem, "alert", "creator:einlesen", { melden: false });
    await run("einlesen", async () => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      // L12: E-Rechnung (XML oder ZUGFeRD) zuerst; die Werte stehen dort eindeutig. Sonst Text aus dem PDF wie bisher.
      const e = await readEInvoiceFile(bytes, { fileName: file.name }).catch((err: unknown) => {
        if (xml || (err instanceof EInvoiceReadError && !["no_xml_in_pdf", "pdf_unreadable", "not_einvoice"].includes(err.code))) {
          throw err instanceof EInvoiceReadError ? new EingabeFehler(eInvoiceReadErrorText(err.code, lang)) : err;
        }
        return null;
      });
      const r = e ? { ...draftSuggestionFromEInvoice(e.data), textLines: 1 } : await readInvoicePdf(bytes, { names: env.deal ? [brandName(env.deal)] : [], lang: env.recipient.language ?? "de" });
      if (!r.textLines) throw new Error(t("In dieser Datei steht kein lesbarer Text (vermutlich gescannt). Bitte die Angaben eintippen; die Datei lädst du im nächsten Schritt hoch.",
        "This file contains no readable text (probably scanned). Please type in the details; you upload the file in the next step."));
      const filled = [
        e ? t(`E-Rechnung (${eInvoiceFormatLabel({ syntax: e.data.syntax, container: e.container, profile: e.data.profile }, lang)})`, `e-invoice (${eInvoiceFormatLabel({ syntax: e.data.syntax, container: e.container, profile: e.data.profile }, lang)})`) : null,
        r.number ? t("Nummer", "number") : null, r.issueDate ? t("Rechnungsdatum", "invoice date") : null,
        r.serviceFrom ? t("Leistungszeitraum", "service period") : null, r.termDays !== null ? t("Zahlungsziel", "payment terms") : null,
        r.lines.length ? t(`${r.lines.length} Position(en)`, `${r.lines.length} item(s)`) : null,
      ].filter((x): x is string => !!x);
      setDraft((d) => d && ({
        ...d,
        number: r.number ?? d.number, issueDate: r.issueDate ?? d.issueDate, serviceFrom: r.serviceFrom ?? d.serviceFrom, serviceTo: r.serviceTo ?? d.serviceTo,
        termDays: r.termDays ?? d.termDays,
        // Beträge der eigenen Rechnung sind schon der eigene Rechnungsbetrag: ohne weiteren Abzug
        ...(r.lines.length ? {
          mode: "share" as const, commissionPercent: null,
          lines: r.lines.map((l) => ({ key: newLineKey(), description: l.description, quantity: l.quantity, unitCents: l.unitCents, commissionFree: purpose === "expenses",
            // Abnahme E-2: Steuersatz je Position aus der E-Rechnung
            ...("rateBp" in l && typeof l.rateBp === "number" ? { rateBp: l.rateBp } : {}) })),
        } : {}),
      }));
      setPriceText({}); setQtyText({});
      setImported({ file, grossCents: r.grossCents, currency: r.currency, filled });
      return true;
    }, t("Rechnung eingelesen. Bitte die Werte prüfen.", "Invoice read. Please check the values."));
  }

  async function useImportedFile() {
    if (!imported || !draft?.invoiceId) return;
    const file = imported.file;
    setUploadStatus(t(`${file.name} wird hochgeladen …`, `Uploading ${file.name} …`));
    const path = await run("upload", () => attachInvoiceFile(api, scope, draft.invoiceId!, file, file.name, true, "uploaded"), t("Rechnung hochgeladen.", "Invoice uploaded."));
    if (path) setPdfMatches(false);
    setUploadStatus(path ? t(`Hochgeladen: ${file.name}`, `Uploaded: ${file.name}`) : null);
  }

  async function submit() {
    setShowCheck(true);
    if (!reviewed || dirty || submitCheck.errors.length || unconfirmed.length || !draft?.invoiceId || eSplit.blocking.length) return;
    // „erneut eingereicht“, wenn die Agentur die Rechnung schon einmal geprüft hat (Korrektur nach Rückgabe)
    const again = !!existing?.reviewed_at || (existing ? ["revision_requested", "rejected"].includes(existing.status) : false);
    const ok = await run("einreichen", () => submitInvoice(api, scope, draft.invoiceId!, { again, comment, confirmPdf: env?.pdfOrigin === "uploaded" && pdfMatches, confirmTax: confirmed.has(TAX_CONFIRM_KEY) }),
      t("Rechnung eingereicht. Deine Agentur prüft sie jetzt.", "Invoice submitted. Your agency is reviewing it now."));
    if (ok !== undefined) navigate(`/creator/rechnungen?rechnung=${draft.invoiceId}`);
  }

  const toggleConfirm = (key: string, on: boolean) => setConfirmed((s) => {
    const n = new Set(s);
    if (on) n.add(key);
    else n.delete(key);
    return n;
  });

  const periodsOfDeal = env.deal ? b.periods.filter((p) => p.deal_id === env.deal!.id).sort((x, y) => x.month.localeCompare(y.month)) : [];
  const selfBilling = b.talent.self_billing_agreed_on ?? null;
  const liveFor = (p: CPeriod) => b.invoices.filter((i) => i.period_id === p.id && i.id !== draft.invoiceId && !["withdrawn", "discarded", "cancelled"].includes(i.status));
  // Beschriftung eines Leistungsmonats (Auswahl und, am Handy, ganz darunter; SICHT-CREATOR-20)
  const monthText = (p: CPeriod) => {
    const inv = liveFor(p);
    const extra = p.invoice_requested_at ? t(` · angefordert am ${fmt(p.invoice_requested_at)}`, ` · requested on ${fmt(p.invoice_requested_at)}`) : inv.length ? ` · ${t("Rechnung", "Invoice")} ${inv.map((i) => i.number ?? t("Entwurf", "Draft")).join(", ")}`
      : p.month.slice(0, 7) > today.slice(0, 7) ? t(" · noch nicht begonnen", " · not started yet") : "";
    return `${monthOf(p.month, lang)}${extra}`;
  };
  const chosenPeriod = periodsOfDeal.find((p) => p.id === draft.periodId) ?? null;
  const gaps = profileGaps(b.talent, profile.data?.payout ?? null, env.rules);
  const attachments = (details.data?.attachments ?? []).filter((a) => a.owner_id === draft.invoiceId);
  // L12: eingereichte E-Rechnung gegen die eigenen Angaben (Nummer, Datum, Beträge, Zeitraum, Steuer-ID, Empfänger); nur Hinweise
  const eAtt = draft.invoiceId ? eInvoiceAttachmentOf(attachments, draft.invoiceId, currentRow?.pdf_path ?? null) : null;
  const expected: EInvoiceExpected = {
    number: draft.number, issueDate: draft.issueDate, serviceFrom: draft.serviceFrom, serviceTo: draft.serviceTo, month: draft.month, currency: cur,
    netCents: comp.totals.netCents, taxCents: comp.totals.vatCents, grossCents: comp.totals.grossCents,
    sellerVatId: b.talent.vat_id, sellerTaxNumber: b.talent.tax_number, buyerName: env.recipient.name,
  };
  const eFindings = eAtt && eAtt.e_invoice.origin === "uploaded" && !dirty ? eInvoiceSummaryDeviations(eAtt.e_invoice, expected, lang) : [];
  // Abnahme E-3: Nummer und Brutto (und Währung) müssen stimmen; sonstige Abweichungen bestätigt der Creator ausdrücklich
  const eSplit = splitEInvoiceFindings(eFindings, lang);
  // E-1 (0448): woher die Werte der E-Rechnung stammen (Server oder noch der Browser)
  const ePruef = eAtt && !dirty ? eInvoicePruefHinweis({ pruefung: eAtt.pruefung ?? null, e_invoice_check_error: eAtt.e_invoice_check_error }, lang) : null;
  // Abnahme E-6: „Bestätige …“ passt nur, wenn es den Haken gibt; gesperrt steht dort, was abweicht
  const submitCheckShown = confirmFindingFor(submitCheck, eSplit, lang);
  const recipientText = [env.recipient.name, ...env.recipient.lines].filter(Boolean).join(", ");
  const rateSource = rate ? (rate.source === "deal" ? t("für diese Kooperation", "for this collaboration") : rate.source === "talent" ? t("in deinem Profil", "in your profile") : t("Standard deiner Agentur", "your agency's standard")) : "";

  return (
    <Frame step={step} reachable={reachable} params={params}>
      {existing && existing.revision_reason && existing.reviewed_at && ["draft", "revision_requested", "rejected"].includes(existing.status) && (
        <div className="revision-note">{t("Hinweis deiner Agentur", "Note from your agency")}: {existing.revision_reason?.trim() || t("Bitte korrigiere die Rechnung.", "Please correct the invoice.")} {t("Dein bisheriger Beleg bleibt im Verlauf erhalten.", "Your previous document stays in the history.")}</div>
      )}

      {step === 1 && (
        <form className="panel stack" onSubmit={(e) => { e.preventDefault(); if (assigned) goto(2); }}>
          {choices.open.length + choices.done.length === 0 && (
            <p>{t("Für dich gibt es in dieser Agentur noch keine Kooperation, zu der du eine Rechnung einreichen kannst. Eine sonstige Rechnung an deine Agentur geht trotzdem.",
              "There is no collaboration in this agency yet that you can invoice. You can still send another invoice to your agency.")}</p>
          )}
          {(
            <>
              {selfBilling && (
                // Befund Creator 11: bei vereinbartem Gutschriftverfahren rechnet die Agentur die Monate ab
                <div className="notice stack tk-gap-sm">
                  <p><strong>{t("Deine Agentur rechnet für dich per Gutschrift ab.", "Your agency bills on your behalf (self-billing).")}</strong></p>
                  <p>{t(`Vereinbart am ${fmt(selfBilling)}. Für deine Monatsleistungen erstellt die Agentur eine Gutschrift in deinem Namen; du findest sie unter „Rechnungen“ zur Prüfung. Eine eigene Rechnung brauchst du nur für Nachberechnungen oder Auslagen.`,
                    `Agreed on ${fmt(selfBilling)}. Your agency issues a self-billed invoice in your name for your monthly services; you will find it under “Invoices” to check. You only need your own invoice for additional billing or expenses.`)}</p>
                </div>
              )}
              <fieldset className="stack tk-gap-sm">
                <legend>{t("Wofür ist die Rechnung?", "What is the invoice for?")}</legend>
                <div className="tk-choice">
                  <label><input type="radio" name="zweck" checked={purpose === "month"} disabled={!!draft.invoiceId || !!selfBilling || !firstDealId} onChange={() => pickPurpose("month")} />{t("Leistung eines Monats", "Services of one month")}{selfBilling ? t(" (rechnet deine Agentur per Gutschrift ab)", " (billed by your agency)") : ""}</label>
                  <label><input type="radio" name="zweck" checked={purpose === "extra"} disabled={!!draft.invoiceId || !firstDealId} onChange={() => pickPurpose("extra")} />{t("Nachberechnung zur Kooperation (z. B. Nutzungsrechte, Bonus)", "Additional billing for the collaboration (e.g. usage rights, bonus)")}</label>
                  <label><input type="radio" name="zweck" checked={purpose === "expenses"} disabled={!!draft.invoiceId || !firstDealId} onChange={() => pickPurpose("expenses")} />{t("Auslagen (Reise, Hotel …) mit Beleg", "Expenses (travel, hotel …) with receipt")}</label>
                  <label><input type="radio" name="zweck" checked={purpose === "other"} disabled={!!draft.invoiceId} onChange={chooseOther} />{t("Sonstige Rechnung an deine Agentur (ohne Kooperation)", "Other invoice to your agency (without a collaboration)")}</label>
                </div>
                {/* PARITAET-R3-10 (bewusst, ENTSCHEIDUNGEN.md): direkt an die Marke nur mit Kooperation; der Weg dahin steht hier */}
                {b.deals.some((d) => isDirect(d)) && (
                  <p className="muted" id="direkt-ohne-kooperation">
                    {t("Zusatzauftrag einer Marke, für den es noch keine Kooperation gibt (z. B. ein Nachdreh)? Rechnungen direkt an eine Marke hängen immer an einer Kooperation, weil daraus Empfänger, Zahlungsziel, Provision und Mahnung kommen. Bitte deine Agentur, den Auftrag anzulegen; danach wählst du hier „Nachberechnung zur Kooperation“. ",
                      "Extra job from a brand that has no collaboration yet (e.g. a reshoot)? Invoices directly to a brand always belong to a collaboration, because recipient, payment terms, commission and dunning come from it. Ask your agency to set up the job; then choose “Additional billing for the collaboration” here. ")}
                    <Link to="/creator/nachrichten">{t("Agentur fragen", "Ask your agency")}</Link>
                  </p>
                )}
              </fieldset>
              {purpose === "other" ? (
                <label>
                  {t("Beschreibung der Leistung", "Description of the service")}
                  <textarea id="beschreibung" rows={3} maxLength={1000} value={draft.note ?? ""} onChange={(e) => patch({ note: e.target.value })} required aria-describedby="beschreibung-hinweis" />
                  <small id="beschreibung-hinweis">{t(`Zum Beispiel ein Auftrag, den du selbst vermittelt hast und den deine Agentur noch nicht angelegt hat. Mindestens ${OTHER_NOTE_MIN} Zeichen; deine Agentur ordnet die Rechnung damit einer Kooperation zu.`,
                    `For example a job you arranged yourself that your agency has not set up yet. At least ${OTHER_NOTE_MIN} characters; your agency uses it to assign the invoice to a collaboration.`)}</small>
                </label>
              ) : (
              <label>
                {t("Kooperation", "Collaboration")}
                <select id="kooperation" value={draft.dealId ?? ""} onChange={(e) => chooseDealAndMonth(e.target.value, null)} required>
                  <option value="" disabled>{t("Bitte wählen", "Please choose")}</option>
                  <optgroup label={t("Laufend", "Ongoing")}>
                    {choices.open.map((c) => <option key={c.deal.id} value={c.deal.id}>{c.label}</option>)}
                  </optgroup>
                  {choices.done.length > 0 && (
                    <optgroup label={t("Abgeschlossen", "Completed")}>
                      {choices.done.map((c) => <option key={c.deal.id} value={c.deal.id}>{c.label}</option>)}
                    </optgroup>
                  )}
                </select>
                {/* passt die Beschriftung (Marke · Titel · Start · Nr.) nicht ins Feld: ganz darunter (SICHT-CREATOR-20, -27) */}
                <SelectFullText selectId="kooperation" text={chosenDeal} />
              </label>
              )}
              {purpose === "other" ? null : purpose === "month" ? (
                <label>
                  {t("Leistungsmonat", "Service month")}
                  <select id="leistungsmonat" value={draft.periodId ?? ""} onChange={(e) => chooseDealAndMonth(draft.dealId!, e.target.value, "month")} required disabled={!periodsOfDeal.length}>
                    {!periodsOfDeal.length && <option value="">{t("Keine Monate hinterlegt", "No months set up")}</option>}
                    {periodsOfDeal.length > 0 && !draft.periodId && <option value="" disabled>{t("Bitte wählen", "Please choose")}</option>}
                    {periodsOfDeal.map((p) => <option key={p.id} value={p.id}>{monthText(p)}</option>)}
                  </select>
                  <SelectFullText selectId="leistungsmonat" text={chosenPeriod ? monthText(chosenPeriod) : null} />
                  {!periodsOfDeal.length && <small>{t("Ohne Monat kannst du oben „Nachberechnung“ wählen.", "Without a month you can choose “Additional billing” above.")}</small>}
                </label>
              ) : (
                <p>{purpose === "extra"
                  ? t("Die Rechnung hängt an der Kooperation, nicht an einem Monat. Den Leistungszeitraum trägst du im nächsten Schritt ein.", "The invoice belongs to the collaboration, not to a month. You enter the service period in the next step.")
                  : t("Auslagen sind provisionsfrei und werden in voller Höhe gestellt. Den Beleg (Hotel, Fahrt …) hängst du im Schritt „Rechnung“ an.", "Expenses are commission-free and are invoiced in full. You attach the receipt (hotel, travel …) in the “Invoice” step.")}</p>
              )}
              {purpose === "month" && env.period?.invoice_requested_at && (
                <div className="notice stack tk-gap-sm">
                  <p><strong>{t("Deine Agentur hat diese Rechnung angefordert.", "Your agency has requested this invoice.")}</strong></p>
                  {env.period.request_note && <p>{env.period.request_note}</p>}
                </div>
              )}
              <p>
                {direkt ? t("Deine Rechnung geht direkt an die Marke: ", "Your invoice goes directly to the brand: ") : t("Deine Rechnung geht an deine Agentur: ", "Your invoice goes to your agency: ")}
                {recipientText}
                {env.recipient.vatId ? ` · ${taxIdLabel(env.recipient.country, lang)} ${env.recipient.vatId}` : ""}.
                {direkt && t(" Du stellst den vollen Betrag; deine Agentur stellt dir ihre Provision getrennt in Rechnung.", " You invoice the full amount; your agency invoices you its commission separately.")}
                {env.recipient.note && ` ${env.recipient.note}`}
              </p>
            </>
          )}
          {profile.data && gaps.blocking.length > 0 && (
            <div className="notice stack tk-gap-sm" role="alert">
              <p><strong>{t("Vervollständige zuerst dein Profil.", "Complete your profile first.")}</strong> {t("Es fehlen", "Missing")}: {gaps.blocking.map((f) => profileFieldLabel(f, lang, b.talent.country)).join(", ")}.</p>
              <Link className="btn" to="/creator/profil">{t("Profil ergänzen", "Complete profile")}</Link>
            </div>
          )}
          {/* Praxistest K-057: Die Agentur beanstandet eine fehlende Steuernummer; der Creator sah den Hinweis bisher nicht */}
          {profile.data && gaps.hints.filter((f) => f !== "iban:invalid").length > 0 && (
            <div className="notice warning stack tk-gap-sm" role="status">
              <p><strong>{t("Auf deiner Rechnung fehlt noch eine Pflichtangabe.", "Your invoice is still missing a mandatory detail.")}</strong> {t("Es fehlt", "Missing")}: {gaps.hints.filter((f) => f !== "iban:invalid").map((f) => profileFieldLabel(f, lang, b.talent.country)).join(", ")}. {t("Ohne sie gibt deine Agentur die Rechnung meist zur Korrektur zurück.", "Without it your agency will usually return the invoice for correction.")}</p>
              <Link className="btn" to="/creator/profil">{t("Im Steuerprofil ergänzen", "Add in your tax profile")}</Link>
            </div>
          )}
          <div className="row">
            <Link className="btn" to="/creator/rechnungen">{t("Abbrechen", "Cancel")}</Link>
            <button className="primary" type="submit" disabled={!assigned}>{t("Weiter", "Next")}</button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form className="panel stack" onSubmit={(e) => { e.preventDefault(); void saveAndContinue(); }} aria-busy={busy === "speichern"}>
          <p>{purpose === "other" ? t("Sonstige Rechnung an deine Agentur, ohne Kooperation", "Other invoice to your agency, without a collaboration")
            : <>{brandName(env.deal)} · {env.deal?.title} · {purpose === "month" ? (draft.month ? monthOf(draft.month, lang) : t("ohne Monat", "no month")) : purpose === "extra" ? t("Nachberechnung", "Additional billing") : t("Auslagen", "Expenses")}</>}</p>
          {env.deal && (
            <details>
              <summary>{t("Kooperation ansehen", "View collaboration")}</summary>
              <ul>
                <li>{env.deal.deliverables?.trim() || t("Umfang folgt von deiner Agentur.", "Scope to follow from your agency.")}</li>
                {env.deal.usage_rights && <li>{env.deal.usage_rights}</li>}
                {periodsOfDeal.map((p) => (
                  <li key={p.id}>{monthOf(p.month, lang)}: <span className="num">{money(p.amount_cents, env.deal!.currency)}</span> {nurAnteil ? t("dein Anteil netto", "your net share") : t("Honorar netto", "net fee")}{p.id === draft.periodId ? t(" · dieser Monat", " · this month") : ""}</li>
                ))}
                {env.extras.map((e) => <li key={e.id}>{e.description}: <span className="num">{money(e.amount_cents, env.deal!.currency)}</span>{e.commissionable ? "" : t(" · provisionsfrei", " · commission-free")}</li>)}
              </ul>
            </details>
          )}
          <div className="notice stack tk-gap-sm" aria-labelledby="einlesen-titel">
            <strong id="einlesen-titel">{t("Rechnung schon geschrieben (etwa in deinem Buchhaltungsprogramm)?", "Invoice already written (e.g. in your accounting software)?")}</strong>
            <label>
              {t("Bestehende Rechnung einlesen (PDF oder E-Rechnung: XRechnung, ZUGFeRD)", "Read existing invoice (PDF or e-invoice: XRechnung, ZUGFeRD)")}
              <input type="file" accept="application/pdf,.xml,application/xml,text/xml" onChange={(e) => void onImport(e)} disabled={busy === "einlesen"} aria-describedby="einlesen-hinweis" />
            </label>
            <small id="einlesen-hinweis">{t("Nummer, Datum, Leistungszeitraum, Zahlungsziel und Positionen werden als Vorschlag eingetragen. Prüfe die Werte; die Datei nimmst du im nächsten Schritt als Original.",
              "Number, date, service period, payment terms and items are filled in as a suggestion. Check the values; you use the file as the original in the next step.")}</small>
            {imported && (
              <p role="status">
                {t(`Aus ${imported.file.name} übernommen: `, `Taken from ${imported.file.name}: `)}{imported.filled.join(", ") || t("nichts Eindeutiges", "nothing definite")}.
                {imported.grossCents !== null && imported.grossCents !== comp.totals.grossCents && ` ${t(`In der Datei stehen ${money(imported.grossCents, imported.currency ?? cur)} brutto, berechnet sind ${money(comp.totals.grossCents, cur)}. Bitte Positionen und Steuer prüfen.`,
                  `The file shows ${money(imported.grossCents, imported.currency ?? cur)} gross, the calculation gives ${money(comp.totals.grossCents, cur)}. Please check items and tax.`)}`}
                {imported.currency && imported.currency !== cur && ` ${t(`Die Datei ist in ${imported.currency}, die Kooperation in ${cur}.`, `The file is in ${imported.currency}, the collaboration in ${cur}.`)}`}
              </p>
            )}
          </div>
          <div className="grid">
            <label>
              {t("Rechnungsnummer", "Invoice number")}
              <input id="rechnungsnummer" type="text" value={draft.number} onChange={(e) => patch({ number: e.target.value })} required autoComplete="off" aria-describedby="nummer-hinweis" />
              <small id="nummer-hinweis">{t("Deine Nummern gelten für dich als Aussteller, über alle Agenturen hinweg.", "Your numbers apply to you as the issuer, across all agencies.")}</small>
            </label>
            <label>
              {t("Rechnungsdatum", "Invoice date")}
              <input type="date" value={draft.issueDate} onChange={(e) => e.target.value && patch({ issueDate: e.target.value })} required />
            </label>
            <label>
              {t("Leistungszeitraum von", "Service period from")}
              <input type="date" value={draft.serviceFrom ?? ""} onChange={(e) => patch({ serviceFrom: e.target.value || null })} required />
            </label>
            <label>
              {t("Leistungszeitraum bis", "Service period to")}
              <input type="date" value={draft.serviceTo ?? ""} onChange={(e) => patch({ serviceTo: e.target.value || null })} required />
            </label>
            <label>
              {t("Zahlungsziel in Tagen", "Payment terms in days")}
              <input type="number" min={0} max={180} step={1} value={draft.termDays ?? ""} onChange={(e) => patch({ termDays: e.target.value === "" ? null : Number(e.target.value) })} required />
              {typeof b.org.talent_payment_terms_days === "number" && <small>{t(`Vorgabe deiner Agentur: ${b.org.talent_payment_terms_days} Tage.`, `Your agency's default: ${b.org.talent_payment_terms_days} days.`)}</small>}
              {b.org.talent_payout_mode === "after_brand_payment" && <small>{t("Deine Agentur zahlt aus, sobald die Marke bezahlt hat; vorher gilt die Auszahlung nicht als überfällig.", "Your agency pays out once the brand has paid; until then the payout is not overdue.")}</small>}
            </label>
            <label>
              {t("Währung", "Currency")}
              <input type="text" value={draft.currency} readOnly aria-describedby="waehrung-hinweis" />
              <small id="waehrung-hinweis">{t("Wie in der Kooperation vereinbart.", "As agreed in the collaboration.")}</small>
            </label>
          </div>

          {purpose !== "expenses" && !ohneAbzug && (
            <fieldset className="stack tk-gap-sm">
              <legend>{t("Beträge", "Amounts")}</legend>
              <div className="tk-choice">
                <label><input type="radio" name="modus" checked={draft.mode === "budget"} onChange={() => patch({ mode: "budget", commissionPercent: draft.commissionPercent ?? rate?.percent ?? null })} />{t("Kooperationsbudget eintragen, die Provision wird abgezogen", "Enter the collaboration budget, the commission is deducted")}</label>
                <label><input type="radio" name="modus" checked={draft.mode === "share"} onChange={() => patch({ mode: "share" })} />{t("Deinen Anteil eintragen (ohne Abzug)", "Enter your share (no deduction)")}</label>
              </div>
              {draft.mode === "budget" && (
                <label>
                  {t("Provision deiner Agentur in Prozent", "Your agency's commission in percent")}
                  <input type="number" min={0} max={100} step={0.01} value={draft.commissionPercent ?? ""} onChange={(e) => patch({ commissionPercent: e.target.value === "" ? null : Number(e.target.value) })} required />
                  <small>{rate ? t(`Hinterlegt: ${pctOf(rate.percent, lang)} (${rateSource}).`, `On file: ${pctOf(rate.percent, lang)} (${rateSource}).`) : t("Für dich ist kein Satz im Portal hinterlegt. Du findest ihn in deinem Vertrag mit der Agentur.", "No rate is on file for you. You will find it in your contract with the agency.")}</small>
                </label>
              )}
            </fieldset>
          )}
          {nurAnteil && purpose !== "expenses" && <p className="notice">{t("Die Beträge sind schon dein Anteil nach der Provision deiner Agentur. Du stellst sie ohne Abzug in Rechnung.", "The amounts are already your share after your agency's commission. You invoice them without any deduction.")}</p>}
          {direkt && <p className="notice">{t("Direkt an die Marke stellst du den vollen Betrag. Deine Agentur stellt dir ihre Provision", "You invoice the brand the full amount. Your agency invoices you its commission")}{rate ? ` (${pctOf(rate.percent, lang)})` : ""}{t(" getrennt in Rechnung.", " separately.")}</p>}

          <div className="tablewrap tk-cardtable tk-lines-wrap">
            <table className="tk-lines">
              <caption className="sr-only">{t("Positionen der Rechnung", "Invoice items")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("Leistung", "Service")}</th>
                  <th scope="col">{t("Menge", "Qty")}</th>
                  <th scope="col">{draft.mode === "budget" ? t("Einzelpreis (Budget, netto)", "Unit price (budget, net)") : t("Einzelpreis netto", "Unit price net")}</th>
                  <th scope="col">{t("Provisionsfrei", "Commission-free")}</th>
                  {rateOptions.length > 0 && <th scope="col">{vatWord(b.talent.country, lang)}</th>}
                  <th scope="col" className="money">{t("Summe netto", "Net total")}</th>
                  <th scope="col"><span className="sr-only">{t("Entfernen", "Remove")}</span></th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((l, i) => {
                  const computed = comp.totals.lines.find((c) => c.index === i);
                  return (
                    <tr key={l.key}>
                      <td data-label={t("Leistung", "Service")}>
                        <input aria-label={t(`Beschreibung Position ${i + 1}`, `Description item ${i + 1}`)} value={l.description} placeholder={t("z. B. 2x Stories", "e.g. 2x stories")} onChange={(e) => patchLine(l.key, { description: e.target.value })} />
                        {/* PARITAET-R3-5: Kurzangabe zur vollständigen Zeile (Format, Plattform, Handle, Marke, Zeitraum) */}
                        <button type="button" className="tk-line-suggest" aria-label={t(`Position ${i + 1} ausformulieren`, `Write out item ${i + 1}`)}
                          title={t("Macht aus „2x Stories“ eine vollständige Zeile mit Plattform, Handle, Marke und Zeitraum", "Turns “2x stories” into a full line with platform, handle, brand and period")}
                          onClick={() => patchLine(l.key, suggestLine({
                            text: l.description, quantity: l.quantity, deal: env.deal, brandName: env.deal ? brandName(env.deal) : null,
                            month: purpose === "month" ? draft.month : null, serviceFrom: draft.serviceFrom, serviceTo: draft.serviceTo,
                            handles: { instagram: b.talent.instagram_handle, tiktok: b.talent.tiktok_handle, youtube: b.talent.youtube_handle },
                            lang: env.recipient.language ?? "de",
                          }))}>{t("Ausformulieren", "Write out")}</button>
                      </td>
                      <td data-label={t("Menge", "Qty")} className="tk-narrow">
                        <input aria-label={t(`Menge Position ${i + 1}`, `Quantity item ${i + 1}`)} inputMode="decimal"
                          value={qtyText[l.key] ?? quantityText(l.quantity, lang === "en" ? "." : ",")}
                          aria-invalid={Number.isNaN(parseQuantityInput(qtyText[l.key] ?? "")) || undefined}
                          onChange={(e) => {
                            const text = e.target.value;
                            setQtyText((q) => ({ ...q, [l.key]: text }));
                            const qty = parseQuantityInput(text);
                            patchLine(l.key, { quantity: qty !== null && Number.isFinite(qty) ? qty : 0 });
                          }} />
                        {qtyText[l.key] !== undefined && Number.isNaN(parseQuantityInput(qtyText[l.key]!)) && <small className="tk-error" role="alert">{t("Nicht lesbar", "Cannot be read")}</small>}
                      </td>
                      <td data-label={t("Einzelpreis", "Unit price")} className="tk-narrow">
                        <input
                          aria-label={t(`Einzelpreis Position ${i + 1}`, `Unit price item ${i + 1}`)}
                          inputMode="decimal"
                          value={priceText[l.key] ?? centsInput(l.unitCents, lang)}
                          aria-invalid={(() => { const c = parseCentsInput(priceText[l.key] ?? ""); return c !== null && (!Number.isFinite(c) || c < 0); })() || undefined}
                          onChange={(e) => {
                            const text = e.target.value;
                            setPriceText((p) => ({ ...p, [l.key]: text }));
                            // Gegenprüfung GB-4: Unlesbares bleibt als Fehler stehen (unreadable oben) statt still 0
                            const c = parseCentsInput(text);
                            patchLine(l.key, { unitCents: c !== null && Number.isFinite(c) && c >= 0 ? c : 0 });
                          }}
                        />
                        {(() => {
                          const text = priceText[l.key];
                          if (text === undefined) return null;
                          const c = parseCentsInput(text);
                          if (c === null) return null;
                          return !Number.isFinite(c) || c < 0
                            ? <small className="tk-error" role="alert">{t("Nicht lesbar", "Cannot be read")}</small>
                            : <small className="muted">{t("Erkannt: ", "Recognised: ")}{money(c, cur)}</small>;
                        })()}
                      </td>
                      <td data-label={t("Provisionsfrei", "Commission-free")}><label className="tk-check"><input aria-label={t(`Position ${i + 1} ist provisionsfrei (Auslage)`, `Item ${i + 1} is commission-free (expense)`)} type="checkbox" checked={l.commissionFree} disabled={purpose === "expenses"} onChange={(e) => patchLine(l.key, { commissionFree: e.target.checked })} /></label></td>
                      {rateOptions.length > 0 && (
                        <td data-label={vatWord(b.talent.country, lang)} className="tk-narrow">
                          <select aria-label={t(`Steuersatz Position ${i + 1}`, `Tax rate item ${i + 1}`)} value={String(lineRate(l))}
                            onChange={(e) => patchLine(l.key, { rateBp: Number(e.target.value) })}>
                            {rateOptions.map((bp) => <option key={bp} value={bp}>{pctOf(bp / 100, lang)}</option>)}
                          </select>
                        </td>
                      )}
                      <td data-label={t("Summe netto", "Net total")}><span className="num">{computed ? money(computed.lineTotalCents, cur) : "–"}</span></td>
                      <td className="tk-cardaction"><button type="button" aria-label={t(`Position ${i + 1} entfernen`, `Remove item ${i + 1}`)} onClick={() => setDraft((d) => d && ({ ...d, lines: d.lines.filter((x) => x.key !== l.key) }))}>✕</button></td>
                    </tr>
                  );
                })}
                {draft.lines.length === 0 && <tr><td colSpan={rateOptions.length > 0 ? 7 : 6}>{t("Noch keine Position. Füge eine hinzu.", "No items yet. Add one.")}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="row">
            <button type="button" onClick={() => setDraft((d) => d && ({ ...d, lines: [...d.lines, { key: newLineKey(), description: "", quantity: 1, unitCents: 0, commissionFree: purpose === "expenses" }] }))}>{t("+ Position", "+ Item")}</button>
            {(articles.data?.length ?? 0) > 0 && (
              <select
                aria-label={t("Aus dem Artikelkatalog hinzufügen", "Add from the item catalogue")}
                value=""
                onChange={(e) => {
                  const a = articles.data?.find((x) => x.id === e.target.value);
                  if (!a) return;
                  setDraft((d) => d && ({ ...d, lines: [...d.lines, { key: newLineKey(), description: a.description?.trim() || a.name, quantity: 1, unitCents: a.default_price_cents ?? 0, commissionFree: purpose === "expenses" || !a.commissionable }] }));
                }}
              >
                <option value="">{t("Aus dem Artikelkatalog …", "From the item catalogue …")}</option>
                {articles.data?.map((a) => <option key={a.id} value={a.id}>{a.name}{a.default_price_cents ? ` · ${money(a.default_price_cents, a.currency ?? cur)}` : ""}</option>)}
              </select>
            )}
            {purpose === "month" && env.deal && env.period && !draft.invoiceId && (
              <button type="button" onClick={() => { setDraft((d) => d && ({ ...d, ...draftStart(env.deal!, env.period!, env.extras, brandName(env.deal), env.recipient.language, rate) })); setPriceText({}); setQtyText({}); }}>
                {t("Vorschlag übernehmen", "Use suggestion")}
              </button>
            )}
          </div>

          <dl className="tk-sums">
            {draft.mode === "budget" && (
              <>
                <dt>{t("Kooperationsbudget netto", "Collaboration budget net")}</dt><dd className="num">{money(comp.totals.rawNetCents, cur)}</dd>
                <dt>{t(`Provision deiner Agentur (${pctOf(comp.commissionPercent, lang)})`, `Your agency's commission (${pctOf(comp.commissionPercent, lang)})`)}</dt><dd className="num">− {money(comp.totals.commissionCents, cur)}</dd>
              </>
            )}
            <dt>{t("Dein Rechnungsbetrag netto", "Your invoice amount net")}</dt><dd className="num">{money(comp.totals.netCents, cur)}</dd>
            {comp.byRate
              ? comp.byRate.map((r) => (
                <Fragment key={r.ratePercent}>
                  <dt>{t(`${vatWord(b.talent.country, lang)} ${pctOf(r.ratePercent, lang)} auf ${money(r.netCents, cur)}`, `${vatWord(b.talent.country, lang)} ${pctOf(r.ratePercent, lang)} on ${money(r.netCents, cur)}`)}</dt><dd className="num">{money(r.vatCents, cur)}</dd>
                </Fragment>
              ))
              : <><dt>{`${vatWord(b.talent.country, lang)} ${pctOf(comp.tax.ratePercent, lang)}`}</dt><dd className="num">{money(comp.totals.vatCents, cur)}</dd></>}
            <dt className="tk-total">{t("Gesamt brutto", "Total gross")}</dt><dd className="num tk-total">{money(comp.totals.grossCents, cur)}</dd>
          </dl>
          {/* Steuerhinweis steht in der Sprache der Rechnung; mit Beschriftung wirkt er nicht wie eine fehlende Übersetzung (SICHT-CREATOR-19) */}
          {comp.notice && <small>{t("Steuerhinweis auf der Rechnung: ", "Tax note on the invoice: ")}<span lang={env.recipient.language ?? "de"}>{comp.notice}</span> {steuerVorschlagHinweis(lang)}</small>}

          {showCheck && <Findings check={saveCheck} confirmed={confirmed} onConfirm={toggleConfirm} />}
          <div className="row">
            <button type="button" onClick={() => goto(1)}>{t("Zurück", "Back")}</button>
            <button type="button" onClick={() => setShowCheck(true)}>{t("Entwurf prüfen", "Check draft")}</button>
            <button className="primary" type="submit" disabled={busy === "speichern"}>{busy === "speichern" ? t("Wird gespeichert …", "Saving …") : t("Weiter", "Next")}</button>
          </div>
          <small>{t("„Weiter“ speichert deinen Entwurf. Du kannst jederzeit später weitermachen.", "“Next” saves your draft. You can continue later at any time.")}</small>
        </form>
      )}

      {step >= 3 && dirty && (
        <div className="notice" role="alert">
          {t("Deine Änderungen aus „Positionen“ sind noch nicht gespeichert.", "Your changes in “Items” are not saved yet.")} <Link to={`/creator/rechnungen/einreichen?${(() => { const n = new URLSearchParams(params); n.set("schritt", "2"); return n.toString(); })()}`}>{t("Zurück zu den Positionen", "Back to the items")}</Link> {t("und mit „Weiter“ speichern.", "and save with “Next”.")}
        </div>
      )}

      {step === 3 && (
        <form className="panel stack" onSubmit={(e) => { e.preventDefault(); if (env.hasPdf) goto(4); }}>
          <div className="upload stack tk-gap-sm">
            <h2>{t("Deine Rechnung", "Your invoice")}</h2>
            <p>{t(`Das Portal erstellt die Rechnung aus deinen Angaben: ${draft.number || "ohne Nummer"} über ${money(comp.totals.grossCents, cur)} brutto an ${env.recipient.name}, mit Steuerhinweis und deinem Zahlungsweg.`,
              `The portal creates the invoice from your details: ${draft.number || "no number"} for ${money(comp.totals.grossCents, cur)} gross to ${env.recipient.name}, with tax note and your payment details.`)}</p>
            {pdfInput && creatorHasQrBill(pdfInput) && (
              <small>{t("Dein Konto liegt in der Schweiz bzw. in Liechtenstein: die Rechnung bekommt unten einen QR-Zahlteil mit Empfangsschein, damit deine Agentur bzw. die Marke per QR-Code zahlen kann.",
                "Your account is in Switzerland or Liechtenstein: the invoice gets a QR payment part with receipt at the bottom, so your agency or the brand can pay by QR code.")}</small>
            )}
            <div className="row">
              <button type="button" className="primary" onClick={() => void generate()} disabled={!pdfInput || busy === "erzeugen" || dirty}>{busy === "erzeugen" ? t("Wird erzeugt …", "Creating …") : env.pdfOrigin === "generated" ? t("Rechnung neu erzeugen", "Create invoice again") : t("Rechnung erzeugen", "Create invoice")}</button>
              <button type="button" onClick={preview} disabled={!pdfInput || dirty}>{t("Vorschau als PDF", "Preview as PDF")}</button>
              {eOffer.download && <button type="button" onClick={() => void downloadEInvoice()} disabled={!pdfInput || dirty}>{t("Als XRechnung herunterladen", "Download as XRechnung")}</button>}
            </div>
            {eOffer.attach && <small>{t("Du bist regelbesteuert und die Rechnung geht an ein Unternehmen in Deutschland: beim Erzeugen legt das Portal zusätzlich eine XRechnung (E-Rechnung) ab. Ab 2027 bzw. 2028 ist sie die Rechnung im Sinne des § 14 UStG.",
              "You are subject to standard VAT and the invoice goes to a business in Germany: when creating it, the portal also stores an XRechnung (e-invoice). From 2027 or 2028 it is the invoice within the meaning of section 14 UStG.")}</small>}
            {eInvoiceNote && <small className="tk-error" role="alert">{eInvoiceNote}</small>}
            {!pdfInput && !dirty && <small>{t("Die gespeicherten Positionen werden geladen …", "Loading the saved items …")}</small>}
          </div>
          <details open={env.pdfOrigin === "uploaded" || !!imported}>
            <summary>{t("Oder eigene Rechnung hochladen (PDF, Bild oder E-Rechnung als XRechnung bzw. ZUGFeRD, maximal 15 MB)", "Or upload your own invoice (PDF, image or e-invoice as XRechnung or ZUGFeRD, max. 15 MB)")}</summary>
            {imported && (
              <div className="row">
                <button type="button" className="primary" onClick={() => void useImportedFile()} disabled={busy === "upload" || !draft.invoiceId || dirty}>
                  {t(`Eingelesene Datei verwenden: ${imported.file.name}`, `Use the file you read in: ${imported.file.name}`)}
                </button>
              </div>
            )}
            <label>
              {t("Datei auswählen", "Choose file")}
              <input type="file" accept={BELEG_ACCEPT} onChange={(e) => void onFile(e, true)} disabled={busy === "upload" || !draft.invoiceId || dirty} />
            </label>
            <small>{t(`Nummer, Datum und Beträge deiner Datei müssen mit deinen Angaben übereinstimmen (${draft.number || "ohne Nummer"} · ${money(comp.totals.grossCents, cur)} brutto). Vor dem Einreichen bestätigst du das.`,
              `The number, date and amounts in your file must match your details (${draft.number || "no number"} · ${money(comp.totals.grossCents, cur)} gross). You confirm this before submitting.`)}</small>
          </details>
          {uploadStatus && <p id="upload-status" role="status">{uploadStatus}</p>}
          <EInvoiceHints findings={eFindings} pruefung={ePruef} />
          <label>
            {purpose === "expenses" ? t("Belege anhängen (Pflicht bei Auslagen, zum Beispiel Hotel- oder Fahrtkosten)", "Attach receipts (required for expenses, for example hotel or travel costs)") : t("Weitere Belege anhängen (zum Beispiel Hotel- oder Fahrtkosten)", "Attach other documents (for example hotel or travel costs)")}
            <input type="file" accept={BELEG_ACCEPT} onChange={(e) => void onFile(e, false)} disabled={busy === "upload" || !draft.invoiceId || dirty || !env.hasPdf} />
            <small>{env.hasPdf ? t("Belege hängen an der Rechnung; die Rechnung selbst bleibt die erzeugte bzw. hochgeladene Datei.", "Documents are attached to the invoice; the invoice itself stays the created or uploaded file.") : t("Erzeuge oder lade zuerst die Rechnung.", "Create or upload the invoice first.")}</small>
          </label>
          {attachments.length > 0 && (
            <div className="stack tk-gap-sm">
              <strong>{t("Abgelegt", "Saved")}</strong>
              {attachments.map((a) => (
                <div key={a.id} className="row between">
                  <span>{a.file_name} <small>{fmt(a.created_at)}{a.storage_path === currentRow?.pdf_path ? (env.pdfOrigin === "generated" ? t(" · gilt als Rechnung (erzeugt)", " · counts as invoice (created)") : t(" · gilt als Rechnung (eigene Datei)", " · counts as invoice (own file)"))
                    : a.e_invoice?.origin === "generated" ? t(" · XRechnung zur erzeugten Rechnung", " · XRechnung of the created invoice") : t(" · weiterer Beleg", " · other document")}
                    {a.e_invoice ? ` · ${t("E-Rechnung", "e-invoice")} ${eInvoiceFormatLabel(a.e_invoice, lang)}` : ""}</small></span>
                  <span className="row">
                    <button type="button" onClick={() => void openDocument(a.storage_path, a.file_name, a.e_invoice && a.mime_type !== "application/pdf" ? { sha256: a.sha256, erwartet: a.category === "invoice" ? expected : null } : undefined)}>{t("Öffnen", "Open")}</button>
                    {a.e_invoice && a.mime_type === "application/pdf" && (
                      <button type="button" onClick={() => void openDocument(a.storage_path, a.file_name, { sha256: a.sha256, erwartet: a.category === "invoice" ? expected : null })}>{t("E-Rechnung ansehen", "View e-invoice")}</button>
                    )}
                    {a.storage_path !== currentRow?.pdf_path && a.e_invoice?.origin !== "generated" && neverSubmitted(a.created_at, currentRow?.submitted_at ?? null) && (
                      <button type="button" disabled={!!busy} aria-label={t(`${a.file_name} entfernen`, `Remove ${a.file_name}`)}
                        onClick={() => void run(`entfernen-${a.id}`, () => removeInvoiceFile(api, a.id), t("Beleg entfernt.", "Document removed."))}>{t("Entfernen", "Remove")}</button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="row">
            <button type="button" onClick={() => goto(2)}>{t("Zurück", "Back")}</button>
            <button className="primary" type="submit" disabled={!env.hasPdf || dirty}>{t("Weiter", "Next")}</button>
          </div>
        </form>
      )}

      {step === 4 && (
        <form className="panel stack" onSubmit={(e) => { e.preventDefault(); void submit(); }} aria-busy={busy === "einreichen"}>
          <h2>{t("Alles richtig?", "Everything correct?")}</h2>
          <p>
            {purpose === "other" ? t("Sonstige Rechnung an deine Agentur, ohne Kooperation", "Other invoice to your agency, without a collaboration")
              : <>{brandName(env.deal)} · {purpose === "month" ? (draft.month ? monthOf(draft.month, lang) : t("ohne Monat", "no month")) : purpose === "extra" ? t("Nachberechnung", "Additional billing") : t("Auslagen", "Expenses")}</>}<br />
            {draft.number || t("ohne Nummer", "no number")} · <span className="num">{money(comp.totals.grossCents, cur)}</span> {t("brutto", "gross")}<br />
            <span className="num">{money(comp.totals.netCents, cur)}</span> {t("netto", "net")} + <span className="num">{money(comp.totals.vatCents, cur)}</span> {vatWord(b.talent.country, lang)}
          </p>
          <p>{t(`Rechnungsdatum ${fmt(draft.issueDate)} · Leistung ${fmt(draft.serviceFrom)} bis ${fmt(draft.serviceTo)} · Zahlungsziel ${draft.termDays ?? "–"} Tage`,
            `Invoice date ${fmt(draft.issueDate)} · service ${fmt(draft.serviceFrom)} to ${fmt(draft.serviceTo)} · payment terms ${draft.termDays ?? "–"} days`)}</p>
          {currentRow?.pdf_path && <div className="row"><button type="button" onClick={() => void openDocument(currentRow.pdf_path!, undefined, eAtt?.storage_path === currentRow.pdf_path ? { sha256: eAtt.sha256, erwartet: expected } : undefined)}>{t("Rechnung ansehen", "View invoice")}</button></div>}
          <EInvoiceHints findings={eFindings} pruefung={ePruef} />
          {eSplit.blocking.length > 0 && (
            <p className="tk-error" role="alert" id="e-rechnung-sperre">
              {t(`Einreichen geht erst, wenn deine Angaben mit der E-Rechnung übereinstimmen: ${eSplit.blocking.map((f) => f.text).join(" ")} Passe die Positionen bzw. die Nummer an oder lade die richtige Datei hoch.`,
                `You can submit once your details match the e-invoice: ${eSplit.blocking.map((f) => f.text).join(" ")} Adjust the items or the number, or upload the correct file.`)}
            </p>
          )}
          {env.pdfOrigin === "uploaded" && eSplit.blocking.length === 0 && (
            <label className="row tk-check"><input type="checkbox" checked={pdfMatches} onChange={(e) => setPdfMatches(e.target.checked)} />
              {eSplit.awareText ?? t(`Nummer ${draft.number || "–"} und Bruttobetrag ${money(comp.totals.grossCents, cur)} stimmen mit meiner hochgeladenen Datei überein.`, `Number ${draft.number || "–"} and gross amount ${money(comp.totals.grossCents, cur)} match my uploaded file.`)}
            </label>
          )}
          {(showCheck || unconfirmed.length > 0 || submitCheck.errors.length > 0) && <Findings check={submitCheckShown} confirmed={confirmed} onConfirm={toggleConfirm} />}
          {/* Pflicht-Haken vor der freiwilligen Nachricht, damit er nicht wie ein Teil davon wirkt (Abnahme 25.10.2026) */}
          <label className="row tk-check"><input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} required />{t("Ich habe die Angaben geprüft.", "I have checked the details.")}</label>
          <label>
            {t("Nachricht an deine Agentur (optional)", "Message to your agency (optional)")}
            <textarea rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
          </label>
          <div className="row">
            <button type="button" onClick={() => goto(3)}>{t("Zurück", "Back")}</button>
            <button type="button" onClick={() => setShowCheck(true)}>{t("Entwurf prüfen", "Check draft")}</button>
            <button className="primary" type="submit" disabled={!reviewed || dirty || busy === "einreichen" || submitCheck.errors.length > 0 || unconfirmed.length > 0 || eSplit.blocking.length > 0}>
              {busy === "einreichen" ? t("Wird eingereicht …", "Submitting …") : t("Rechnung einreichen", "Submit invoice")}
            </button>
          </div>
          {!reviewed && <small className="muted" id="einreichen-grund">{t("Zum Einreichen bitte bestätigen: Ich habe die Angaben geprüft.", "To submit, please confirm: I have checked the details.")}</small>}
          <Link to={`/creator/rechnungen?rechnung=${draft.invoiceId}`}>{t("Als Entwurf lassen und zur Statusvorschau", "Keep as draft and go to status")}</Link>
        </form>
      )}
      <DemoNote />
    </Frame>
  );
}

function Frame({ step, reachable, params, children }: { step: number; reachable: number; params: URLSearchParams; children: ReactNode }) {
  const t = useT();
  const steps = [t("Zuordnung", "Assignment"), t("Positionen", "Items"), t("Rechnung", "Invoice"), t("Prüfen", "Review")];
  const to = (n: number) => {
    const next = new URLSearchParams(params);
    next.set("schritt", String(n));
    return `/creator/rechnungen/einreichen?${next.toString()}`;
  };
  return (
    <main id="main" className="content tk-cr tk-cr-einreichen">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{t("Rechnung einreichen", "Submit invoice")}</h1>
          <p>{t(`Schritt ${step} von ${steps.length}`, `Step ${step} of ${steps.length}`)}</p>
        </div>
        <Link className="btn" to="/creator/rechnungen">{t("Zu deinen Rechnungen", "Go to your invoices")}</Link>
      </div>
      <nav className="steps" aria-label={t("Schritte", "Steps")}>
        {steps.map((label, i) => {
          const n = i + 1;
          const current = n === step;
          return n <= reachable || current ? (
            <Link key={label} className={current ? "current" : ""} aria-current={current ? "step" : undefined} to={to(n)}>{n}. {label}</Link>
          ) : (
            <a key={label} className="" aria-disabled="true">{n}. {label}</a>
          );
        })}
      </nav>
      {children}
    </main>
  );
}

/** L12: Abweichungen der eingereichten E-Rechnung von den Angaben (Hinweis, keine Sperre) */
function EInvoiceHints({ findings, pruefung }: { findings: readonly EInvoiceFinding[]; pruefung?: string | null }) {
  const t = useT();
  if (!findings.length) return pruefung ? <p className="muted" role="status" id="e-rechnung-pruefung"><small>{pruefung}</small></p> : null;
  return (
    <div className="notice stack tk-gap-sm" role="status" id="e-rechnung-abweichungen">
      <strong>{t("Deine E-Rechnung weicht von deinen Angaben ab", "Your e-invoice differs from your details")}</strong>
      <ul className="tk-findings">{findings.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
      <small>{t("Maßgeblich ist die E-Rechnung. Bitte gleiche deine Angaben an oder lade die richtige Datei hoch.", "The e-invoice is authoritative. Please align your details or upload the correct file.")}</small>
      {pruefung && <small id="e-rechnung-pruefung">{pruefung}</small>}
    </div>
  );
}

function Findings({ check, confirmed, onConfirm }: { check: DraftCheck; confirmed: ReadonlySet<string>; onConfirm: (key: string, on: boolean) => void }) {
  const t = useT();
  const none = !check.errors.length && !check.questions.length && !check.hints.length;
  const uniq = (list: Message[]) => list.filter((m, i) => list.findIndex((x) => x.text === m.text) === i);
  return (
    <div className="stack tk-gap-sm" role="status">
      {none && <p>{t("Keine Auffälligkeiten.", "No issues found.")}</p>}
      {check.errors.length > 0 && (
        <>
          <strong>{t("Bitte ergänzen", "Please complete")}</strong>
          <ul className="tk-findings tk-errors">{uniq(check.errors).map((m) => <li key={`${m.key}${m.text}`}>{m.text}</li>)}</ul>
        </>
      )}
      {check.questions.length > 0 && (
        <>
          <strong>{t("Rückfragen", "Questions")}</strong>
          {uniq(check.questions).map((m) => (
            <label key={m.key} className="row tk-check"><input type="checkbox" checked={confirmed.has(m.key)} onChange={(e) => onConfirm(m.key, e.target.checked)} />{m.text}</label>
          ))}
        </>
      )}
      {check.hints.length > 0 && (
        <>
          <strong>{t("Hinweise", "Notes")}</strong>
          <ul className="tk-findings">{uniq(check.hints).map((m) => <li key={`${m.key}${m.text}`}>{m.text}</li>)}</ul>
        </>
      )}
    </div>
  );
}

